import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  AnimalType,
  MedType,
  IndicationType
} from '../types/dosage-types';
import { TreatmentGuide, FAQ } from '../types/treatment-guide-types';
import { getTreatmentGuide } from '../data/treatment-guide-data';

@customElement('treatment-guide')
export class TreatmentGuideComponent extends LitElement {
  @property({ type: String }) animalType: AnimalType = AnimalType.DOG;
  @property({ type: String }) medType: MedType = MedType.TOLTRAZURIL;
  @property({ type: String }) indicationType: IndicationType = IndicationType.TREATMENT;

  @state() private expandedFaq: number | null = null;
  @state() private treatmentGuide: TreatmentGuide | null = null;

  // Use light DOM for Tailwind CSS
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadTreatmentGuide();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('animalType') ||
        changedProperties.has('medType') ||
        changedProperties.has('indicationType')) {
      this.loadTreatmentGuide();
    }
  }

  private loadTreatmentGuide() {
    this.treatmentGuide = getTreatmentGuide(this.animalType, this.medType, this.indicationType);
  }

  private toggleFaq(index: number) {
    this.expandedFaq = this.expandedFaq === index ? null : index;
  }

  private getAnimalName(animalType: AnimalType): string {
    const names = {
      [AnimalType.DOG]: 'Dogs',
      [AnimalType.CAT]: 'Cats',
      [AnimalType.CHICKEN]: 'Chickens',
      [AnimalType.GOAT]: 'Goats',
      [AnimalType.HORSE]: 'Horses',
      [AnimalType.RABBIT]: 'Rabbits',
      [AnimalType.SHEEP]: 'Sheep',
      [AnimalType.PIG]: 'Pigs',
      [AnimalType.CATTLE]: 'Cattle'
    };
    return names[animalType] || animalType;
  }

  private getMedicationName(medType: MedType): string {
    const names = {
      [MedType.TOLTRAZURIL]: 'Toltrazuril',
      [MedType.SULFADIMETHOXINE]: 'Sulfadimethoxine',
      [MedType.FENBENDAZOLE]: 'Fenbendazole',
      [MedType.OMEPRAZOLE]: 'Omeprazole',
      [MedType.IVERMECTIN]: 'Ivermectin',
      [MedType.ALBENDAZOLE]: 'Albendazole',
      [MedType.METRONIDAZOLE]: 'Metronidazole',
      [MedType.AMPROLIUM]: 'Amprolium'
    };
    return names[medType] || medType;
  }

  private getPriorityClass(priority: 'high' | 'medium' | 'low'): string {
    const classes = {
      high: 'border-red-200 bg-red-50',
      medium: 'border-yellow-200 bg-yellow-50',
      low: 'border-blue-200 bg-blue-50'
    };
    return classes[priority];
  }

  private getPriorityIcon(priority: 'high' | 'medium' | 'low'): string {
    const icons = {
      high: '🔴',
      medium: '🟡',
      low: '🔵'
    };
    return icons[priority];
  }

  private getCategoryColor(category: string): string {
    const colors = {
      administration: 'bg-blue-100 text-blue-800',
      safety: 'bg-red-100 text-red-800',
      effectiveness: 'bg-green-100 text-green-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.general;
  }

  private renderHeader() {
    if (!this.treatmentGuide) return '';

    return html`
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold text-gray-900 mb-3">
          ${this.getMedicationName(this.medType)} Treatment Guide
        </h2>
        <p class="text-lg text-gray-600">
          Complete administration instructions for ${this.getAnimalName(this.animalType).toLowerCase()}
        </p>
      </div>
    `;
  }

  private renderAdministrationSteps() {
    if (!this.treatmentGuide?.administrationSteps.length) return '';

    return html`
      <div class="mb-12">
        <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="text-3xl mr-3">📋</span>
          Step-by-Step Administration
        </h3>

        <div class="space-y-6">
          ${this.treatmentGuide.administrationSteps.map((step, index) => html`
            <div class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span class="text-2xl">${step.icon}</span>
                  </div>
                </div>

                <div class="flex-1">
                  <div class="flex items-center mb-2">
                    <span class="bg-blue-600 text-white text-sm font-bold px-2 py-1 rounded mr-3">
                      Step ${step.step}
                    </span>
                    <h4 class="text-lg font-semibold text-gray-900">${step.title}</h4>
                  </div>

                  <p class="text-gray-700 mb-4 leading-relaxed">${step.description}</p>

                  ${step.tips?.length ? html`
                    <div class="mb-3">
                      <h5 class="text-sm font-semibold text-green-700 mb-2">💡 Helpful Tips:</h5>
                      <ul class="text-sm text-green-700 space-y-1">
                        ${step.tips.map(tip => html`<li class="flex items-start"><span class="mr-2">•</span>${tip}</li>`)}
                      </ul>
                    </div>
                  ` : ''}

                  ${step.warnings?.length ? html`
                    <div>
                      <h5 class="text-sm font-semibold text-red-700 mb-2">⚠️ Important Warnings:</h5>
                      <ul class="text-sm text-red-700 space-y-1">
                        ${step.warnings.map(warning => html`<li class="flex items-start"><span class="mr-2">•</span>${warning}</li>`)}
                      </ul>
                    </div>
                  ` : ''}
                </div>
              </div>
            </div>
          `)}
        </div>

        ${this.treatmentGuide.preparationNotes?.length ? html`
          <div class="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 class="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <span class="text-2xl mr-2">📝</span>
              Preparation Notes
            </h4>
            <ul class="text-blue-800 space-y-2">
              ${this.treatmentGuide.preparationNotes.map(note => html`
                <li class="flex items-start">
                  <span class="text-blue-600 mr-2">•</span>
                  ${note}
                </li>
              `)}
            </ul>
          </div>
        ` : ''}
      </div>
    `;
  }

  private renderTips() {
    if (!this.treatmentGuide?.tips.length) return '';

    return html`
      <div class="mb-12">
        <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="text-3xl mr-3">💡</span>
          Helpful Tips & Best Practices
        </h3>

        <div class="grid md:grid-cols-2 gap-6">
          ${this.treatmentGuide.tips.map(tip => html`
            <div class="border-2 rounded-xl p-6 transition-all duration-300 hover:shadow-md hover:-translate-y-1 ${this.getPriorityClass(tip.priority)}">
              <div class="flex items-start space-x-3">
                <div class="flex-shrink-0">
                  <span class="text-2xl">${tip.icon}</span>
                </div>
                <div class="flex-1">
                  <div class="flex items-center mb-2">
                    <h4 class="text-lg font-semibold text-gray-900 mr-2">${tip.title}</h4>
                    <span class="text-sm">${this.getPriorityIcon(tip.priority)}</span>
                  </div>
                  <p class="text-gray-700 leading-relaxed">${tip.description}</p>
                </div>
              </div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private renderFAQs() {
    if (!this.treatmentGuide?.faqs.length) return '';

    return html`
      <div class="mb-12">
        <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="text-3xl mr-3">❓</span>
          Frequently Asked Questions
        </h3>

        <div class="space-y-4">
          ${this.treatmentGuide.faqs.map((faq, index) => html`
            <div class="bg-white border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md">
              <button
                @click=${() => this.toggleFaq(index)}
                class="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <span class="text-xs font-medium px-2 py-1 rounded ${this.getCategoryColor(faq.category)}">
                      ${faq.category.charAt(0).toUpperCase() + faq.category.slice(1)}
                    </span>
                    <h4 class="text-lg font-medium text-gray-900">${faq.question}</h4>
                  </div>
                  <svg
                    class="w-5 h-5 text-gray-500 transition-transform duration-200 ${this.expandedFaq === index ? 'transform rotate-180' : ''}"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              ${this.expandedFaq === index ? html`
                <div class="px-6 pb-4 border-t border-gray-100 bg-gray-50">
                  <p class="text-gray-700 leading-relaxed pt-4">${faq.answer}</p>
                </div>
              ` : ''}
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private renderNextSteps() {
    if (!this.treatmentGuide?.nextSteps.length) return '';

    return html`
      <div class="mb-12">
        <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="text-3xl mr-3">🎯</span>
          Next Steps & Follow-up Care
        </h3>

        <div class="grid md:grid-cols-2 gap-6">
          ${this.treatmentGuide.nextSteps.map(step => html`
            <div class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-300">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span class="text-xl">${step.icon}</span>
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-lg font-semibold text-gray-900 mb-2">${step.title}</h4>
                  <p class="text-gray-700 mb-3 leading-relaxed">${step.description}</p>
                  <div class="flex items-center justify-between text-sm">
                    <span class="bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                      ${step.timeframe}
                    </span>
                    <span class="text-gray-600">${step.action}</span>
                  </div>
                </div>
              </div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  private renderNotAvailable() {
    return html`
      <div class="text-center py-12">
        <div class="text-6xl mb-4">📚</div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Treatment Guide Coming Soon</h3>
        <p class="text-gray-600">
          We're working on creating a comprehensive treatment guide for
          ${this.getMedicationName(this.medType)} in ${this.getAnimalName(this.animalType).toLowerCase()}.
        </p>
        <p class="text-gray-500 text-sm mt-4">
          Always consult with your veterinarian for specific treatment guidance.
        </p>
      </div>
    `;
  }

  render() {
    if (!this.treatmentGuide) {
      return html`
        <div class="max-w-4xl mx-auto bg-gray-50 rounded-2xl p-8">
          ${this.renderNotAvailable()}
        </div>
      `;
    }

    return html`
      <div class="max-w-4xl mx-auto bg-gray-50 rounded-2xl p-8 vetri-animate-fade-in">
        ${this.renderHeader()}
        ${this.renderAdministrationSteps()}
        ${this.renderTips()}
        ${this.renderFAQs()}
        ${this.renderNextSteps()}

        <!-- Disclaimer -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <h4 class="text-lg font-semibold text-yellow-800 mb-3 flex items-center">
            <span class="text-2xl mr-2">⚖️</span>
            Professional Disclaimer
          </h4>
          <p class="text-yellow-700 text-sm leading-relaxed">
            This treatment guide provides general information and should not replace professional veterinary advice.
            Always consult with a qualified veterinarian before starting any treatment regimen. Individual animals
            may require different dosing or treatment protocols based on their specific health status and circumstances.
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'treatment-guide': TreatmentGuideComponent;
  }
}